import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components';
import { handleCustomerError } from '@mobileUtils/helper';
import { Comments } from '@typeDefinitions/comments';
import {
  AddOrEditCommentPayload,
  CreditIncreasePayload,
  CustomerGeneralDocuments,
  CustomersApiResponse,
  MobileGroup as Group,
  ICustomerDetails,
} from '@typeDefinitions/customerTypes';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';

import { HistoryGridPayload, OrderHistoryModel } from './store';

export const fetchCustomers = async (payload: Record<string, unknown>) => {
  try {
    const response: V4ApiResponse<CustomersApiResponse> = await apiClient.post(
      'Customer/searchcustomer',
      payload,
    );
    return response;
  } catch (error) {
    handleCustomerError(error, 'Failed to get customer grid data');
  }
};

export const fetchCustomerData = async (customerId: string) => {
  try {
    const response: V4ApiResponse<ICustomerDetails> = await apiClient.get(
      `Customer/getcustomerdata/${customerId}`,
    );
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleCustomerError(error, 'Failed to fetch customer detail Data');
  }
};

export const searchCustomers = async (searchText: string, skip: number) => {
  try {
    const filter = `
      startswith(tolower(Name), '${searchText}') eq true or
      startswith(tolower(AccountManager), '${searchText}') eq true or
      startswith(tolower(SalesManager), '${searchText}') eq true or
      startswith(tolower(Office), '${searchText}') eq true
    `;

    const response: V4ApiResponse<CustomersApiResponse> = await apiClient.get(
      'Customer/search',
      {
        params: {
          $top: 40,
          $filter: filter.replace(/\s+/g, ' '),
          $orderby: 'CreatedDate desc',
          $skip: skip,
        },
      },
    );

    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    handleCustomerError(error, 'Failed to search customers');
  }
};

export async function fetchCustomerOfficeGroupsCall(officeId: number) {
  try {
    const response: V4ApiResponse<Group[]> = await apiClient.get(
      `Office/Groups/${officeId}?Id=${officeId}`,
    );
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'There was a issue fetching office groups.');
    handleToast(ToastType.Error, e.message);
  }
}

export const fetchCustomerDetailsDocuments = async (customerId: string) => {
  try {
    const response: V4ApiResponse<CustomerGeneralDocuments[]> =
      await apiClient.get(`customer/getdocument/${customerId}`);
    return response;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch customer document data');
    handleToast(ToastType.Error, e.message);
  }
};

export async function addCustomerGeneralComment(
  payload: AddOrEditCommentPayload,
) {
  try {
    const response: V4ApiResponse<Comments> = await apiClient.post(
      'Customer/Comment',
      payload,
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Adding carrier comments failed');
    handleToast(ToastType.Error, error.message);
  }
}

export const updateCustomerDetailsSave = async (payload: ICustomerDetails) => {
  try {
    const res: V4ApiResponse<ICustomerDetails> = await apiClient.put(
      'Customer',
      payload,
    );
    if (Boolean(res.isSuccess)) {
      handleToast(ToastType.Success, 'Customer changes saved');
      return res.value;
    }
  } catch (error: unknown) {
    handleCustomerError(error, 'Failed to update customer details');
  }
};

export const fetchOrderHistoryData = async (payload: HistoryGridPayload) => {
  try {
    const response: V4ApiResponse<{
      data: OrderHistoryModel[];
      totalCount: number;
    }> = await apiClient.post('customer/customerorderhistory', payload);
    if (response.isSuccess) {
      return response.value;
    }
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch Order History Data');
    handleToast(ToastType.Error, e.message);
  }
};

export async function deleteCustomerGeneralComment(commentId: number) {
  try {
    const response: V4ApiResponse<Comments> = await apiClient.delete(
      `Customer/Comment/${commentId}`,
    );
    return response.isSuccess;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to delete comment');
    handleToast(ToastType.Error, e.message);
  }
}

export async function creditIncreaseRequest(payload: CreditIncreasePayload) {
  try {
    const res: V4ApiResponse<null> = await apiClient.post(
      'Request/CreditIncrease',
      payload,
    );
    return res;
  } catch (error: unknown) {
    const e = createError(error, 'Failed to increase credit');
    handleToast(ToastType.Error, e.message);
  }
}
